import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import RuleHistory from "pages/PayerRules/components/RuleHistory";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetSequestrationRulesetQuery,
  useUpdateSequestrationRulesetMutation,
} from "store/queries/sequestrationRulesets";

import {
  ISequestrationRulesetFormData,
  INITIAL_SEQUESTRATION_RULESET_FORM_DATA,
  SequestrationRulesetForm,
} from "./components/SequestrationRulesetForm";

export function EditSequestrationRulesetPage() {
  useDocumentTitle("Edit Sequester Rule");
  const navigate = useNavigate();
  const { id = "" } = useParams<{ id: string }>();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const { data: response, isLoading } = useGetSequestrationRulesetQuery(id, {
    skip: !id,
  });
  const [updateRuleset] = useUpdateSequestrationRulesetMutation();
  const [formData, setFormData] = useState<ISequestrationRulesetFormData>(
    INITIAL_SEQUESTRATION_RULESET_FORM_DATA
  );

  // Map the fetched row into the form's multi-select-shaped state.
  useEffect(() => {
    const row = response?.data;
    if (!row) return;
    setFormData({
      providerIds: row.providerId ? [row.providerId] : [],
      payerIds: row.payerId != null ? [row.payerId] : [],
      percent: Number(row.percent),
      isDefault: row.isDefault,
      contractLanguage: row.contractLanguage ?? "",
    });
  }, [response]);

  const handleSubmit = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      await updateRuleset({
        id,
        body: {
          // Default rows: backend ignores scope on update for default; non-default rows
          // can be re-scoped via the form's single-row dropdowns.
          providerId: formData.isDefault
            ? null
            : formData.providerIds[0] ?? null,
          payerId: formData.isDefault ? null : formData.payerIds[0] ?? null,
          percent: formData.percent,
          contractLanguage: formData.contractLanguage || null,
        },
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Sequester rule updated",
      });
      navigate("/payer-rules");
    } catch (err) {
      const detail =
        (err as { data?: { message?: string } })?.data?.message ??
        "Failed to update sequester rule";
      toastElement?.show({
        severity: "error",
        summary: "Could not update sequester rule",
        detail,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return null;

  return (
    <>
      <SequestrationRulesetForm
        title="Edit Sequester Rule"
        submitLabel="Save"
        mode="edit"
        // Default row scope is locked (it must stay at both-nulls); only percent +
        // contractLanguage are editable.
        scopeLocked={formData.isDefault}
        formData={formData}
        onChange={setFormData}
        onSubmit={handleSubmit}
      />
      <RuleHistory histories={response?.data.history} />
    </>
  );
}
